import { Box, Flex, Image, Modal, Rating, Skeleton, Stack, Text, useMantineTheme } from '@mantine/core';
import { useDisclosure, useMediaQuery } from '@mantine/hooks';
import type { FC } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { direction, foodDetails, locationIcon } from '../../assets';
import { convertTo12HourFormat, openGoogleMaps } from '../../config/Helper';
import { ROUTES } from '../../config/RoutesName';
import { useGetRestaurantBusinessHours } from '../../services/restaurant/restaurant.service';
import { ModalCloseButton } from '../Cart/style';
import { useInfoHeaderStyles } from './styles';

export interface IRestaurantDetails {
  name: string;
  full_address: string;
  business_hour: BusinessHour;
  is_restaurant_open: boolean;
  rating: Rating;
}

export interface BusinessHour {
  opening_time: string;
  closing_time: string;
}

export interface Rating {
  average_rating: string;
  count: number;
}

type Props = {
  data: {
    restaurantName: string;
    rating: number;
    ratingCount: number;
    openingHours: string;
    fullAddress: string;
    restaurantOpen: boolean;
    backgroundImage: string;
  };
  restaurantDetails: IRestaurantDetails;
  isLoading: boolean;
  isCategorySuccess: boolean;
};

const InfoHeader: FC<Props> = ({
  data,
  restaurantDetails,
  isLoading: isResturantDetailsLoadding,
  isCategorySuccess,
}) => {
  const { classes } = useInfoHeaderStyles();
  const navigate = useNavigate();
  const mantinetheme = useMantineTheme();
  const isMobile = useMediaQuery(`(max-width: ${mantinetheme.breakpoints.sm})`);
  const { restaurantId } = useParams<{ restaurantId: string }>();
  const [opened, { open, close }] = useDisclosure(false);
  const { classes: closeButtonStyle } = ModalCloseButton();
  // const today = dayJsExtend().locale('America/Los_Angeles').day();
  const { data: restaurantBusinessHourData, isLoading } = useGetRestaurantBusinessHours({
    enableIf: restaurantId !== undefined && isCategorySuccess,
    restaurantId: JSON.stringify(parseInt(restaurantId!)),
  });

  // const activeShiftLabel = getActiveShiftLabel(restaurantBusinessHourData?.data[today]?.shifts ?? []);
  const openingTime = restaurantDetails?.business_hour?.opening_time;
  const closingTime = restaurantDetails?.business_hour?.closing_time;
  const businessHourLabel =
    openingTime && closingTime
      ? `${restaurantDetails.business_hour.opening_time} - ${restaurantDetails.business_hour.closing_time}`
      : null;
  const openingHours = !data?.openingHours?.includes('undefined') ? data?.openingHours : undefined;
  const openHoursLabel = businessHourLabel ?? openingHours;
  const hasOpenHours = restaurantDetails?.is_restaurant_open && Boolean(openHoursLabel);

  return (
    <>
      <Box className={classes.container}>
        <Image src={data?.backgroundImage?.length ? data?.backgroundImage : foodDetails} height={180} />

        <Box className={classes.restaurantCardBox}>
          <Flex direction='column' gap={0}>
            {isResturantDetailsLoadding || !isCategorySuccess ? (
              <>
                <Skeleton width={isMobile ? 180 : 260} height={isMobile ? 18 : 24} mb={isMobile ? 8 : 16} />

                <Flex gap={8} align='center' mb={isMobile ? 8 : 16}>
                  <Skeleton width={110} height={14} />
                  <Skeleton width={45} height={14} />
                </Flex>

                <Flex justify='space-between' align='center' mb={isMobile ? 6 : 10}>
                  <Flex gap={8} align='center'>
                    <Skeleton width={90} height={13} />
                    <Skeleton width={120} height={13} />
                  </Flex>
                </Flex>

                <Flex align='center' justify='space-between' gap={8}>
                  <Flex align='center' style={{ flex: 1 }} mr={8}>
                    <Skeleton circle width={14} height={14} mr={6} />
                    <Skeleton height={13} style={{ flex: 1 }} />
                  </Flex>
                  <Skeleton circle width={28} height={28} />
                </Flex>
              </>
            ) : (
              <>
                <Text c='magenta.9' fz={isMobile ? 18 : 24} fw={500} lh={1} mb={isMobile ? 8 : 16}>
                  {restaurantDetails?.name ?? data?.restaurantName}
                </Text>

                <Flex gap={4} align='center' mb={isMobile ? 8 : 16}>
                  <Rating
                    readOnly
                    classNames={{ symbolBody: classes.rating }}
                    fractions={4}
                    value={Number(restaurantDetails?.rating?.average_rating ?? data?.rating)}
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(ROUTES.REVIEWS + `/${restaurantId}`);
                    }}
                  />
                  <Text fz={14} fw={500} lh={1}>
                    ({restaurantDetails?.rating?.count ?? data?.ratingCount})
                  </Text>
                </Flex>
                <Flex justify='space-between' align='center' mb={isMobile ? 6 : 10}>
                  <Flex gap={4}>
                    {hasOpenHours ? (
                      <>
                        <Text fz={13} fw={500} lh={1}>
                          We&apos;re open :
                        </Text>

                        <Text
                          fz={13}
                          fw={500}
                          lh={1}
                          c='blue'
                          style={{
                            cursor: 'pointer',
                          }}
                          onClick={(e) => {
                            e.stopPropagation(), open();
                          }}>
                          {openHoursLabel}
                        </Text>
                      </>
                    ) : (
                      <Text c='magenta.9' fz={16} fw={500} lh={1}>
                        Closed
                      </Text>
                    )}
                  </Flex>
                </Flex>
                <Flex align='center' justify='space-between' gap={4}>
                  <Flex
                    align='center'
                    sx={{
                      cursor: 'pointer',
                    }}
                    onClick={() => openGoogleMaps(restaurantDetails?.full_address ?? data?.fullAddress)}>
                    <Image src={locationIcon} width={14} height={14} fit='cover' mr={4} />
                    <Text fz={13} lineClamp={1} lh={1}>
                      {restaurantDetails?.full_address ?? data?.fullAddress}
                    </Text>
                  </Flex>
                  <Image withPlaceholder src={direction} width={28} height={28} />
                </Flex>
              </>
            )}
          </Flex>
        </Box>
      </Box>
      <Modal opened={opened} onClose={close} title='Business Hours' centered className={closeButtonStyle.closeButton}>
        <Stack spacing={10} align='center' justify='center'>
          {isLoading &&
            Array(7)
              .fill('')
              .map((_, index) => (
                <Flex justify='space-between' align='flex-start' mb={15} w='100%' key={index}>
                  <Skeleton h={13} w={75} />
                  <Flex direction='column'>
                    <Skeleton h={13} w={150} />
                  </Flex>
                </Flex>
              ))}
          {!isLoading &&
            restaurantBusinessHourData?.data.map((d: any, index: number) => (
              // <Card key={index} w={'100%'} shadow='sm'>
              //   <Text fz={18} fw={500}>
              //     {d?.day?.name}
              //   </Text>
              //   <Flex gap={4}>
              //     {d?.shifts.map((shift: any, index: number) => (
              //       <Text key={index} fz={14} fw={450}>
              //         {shift?.name}: {shift?.start_time} - {shift?.end_time}
              //       </Text>
              //     ))}
              //   </Flex>
              // </Card>

              <Flex key={d?.day?.id} justify='space-between' align='flex-start' mb={10} w='100%'>
                <Text fw='bold' w={50}>
                  {d?.day?.name}
                </Text>
                <Flex direction='column'>
                  {!d?.shifts?.length && (
                    <Text c='#D61D4F' fw='bold' ml={10}>
                      Closed
                    </Text>
                  )}
                  {!!d?.shifts?.length &&
                    d?.shifts.map((shift: any, index: number) => (
                      <Text ta='right' key={shift?.business_hour_id}>
                        {convertTo12HourFormat(shift?.start_time)} - {convertTo12HourFormat(shift?.end_time)}
                      </Text>
                    ))}
                </Flex>
              </Flex>
            ))}
        </Stack>
      </Modal>
    </>
  );
};

export default InfoHeader;
